import { MAP_INVENTORY_QUANTILE_STEPS, MAP_INVENTORY_SCREENS_OPACITY } from "form/inventory/model"
import { getImpressionsStepBackgroundColors, getImpressionsStepTextColors } from "form/inventory/tools/common"
import { Map } from "mapbox-gl"
import screenImage1 from "resources/images/screens/screen-landscape-fill-1.png"
import screenImage10 from "resources/images/screens/screen-landscape-fill-10.png"
import screenImage2 from "resources/images/screens/screen-landscape-fill-2.png"
import screenImage3 from "resources/images/screens/screen-landscape-fill-3.png"
import screenImage4 from "resources/images/screens/screen-landscape-fill-4.png"
import screenImage5 from "resources/images/screens/screen-landscape-fill-5.png"
import screenImage6 from "resources/images/screens/screen-landscape-fill-6.png"
import screenImage7 from "resources/images/screens/screen-landscape-fill-7.png"
import screenImage8 from "resources/images/screens/screen-landscape-fill-8.png"
import screenImage9 from "resources/images/screens/screen-landscape-fill-9.png"
import { MAP_COLOR, THEME_PALETTE_SPECIAL_TEXT_COLOR } from "ui/model"
import {
  CURSOR_POINTER,
  DEFAULT_SCREENS_CLUSTER_RADIUS,
  IMapImage,
  SCREENS_CLUSTER_CIRCLE_LAYER_ID,
  SCREENS_CLUSTER_TEXT_LAYER_ID,
  SCREENS_POINT_LAYER_ID,
  SCREENS_SOURCE_ID,
  SCREEN_IMAGE_10_ID,
  SCREEN_IMAGE_1_ID,
  SCREEN_IMAGE_2_ID,
  SCREEN_IMAGE_3_ID,
  SCREEN_IMAGE_4_ID,
  SCREEN_IMAGE_5_ID,
  SCREEN_IMAGE_6_ID,
  SCREEN_IMAGE_7_ID,
  SCREEN_IMAGE_8_ID,
  SCREEN_IMAGE_9_ID,
  SCREEN_IMAGE_ID_PREFIX
} from "../model"
import { state } from "../state"
import { getMapMouseCursor, setMapMouseCursor } from "./common"
import { refreshInventoryMapData } from "./inventory"
import { closeInventoryMapPopup, isInventoryMapPopupOpened, openInventoryScreenMapPopup } from "./inventory-popup"

export function activateInventoryScreensView(noRefresh?: boolean): void {
  const { map } = state.inventory
  if (!map) {
    return
  }
  if (!noRefresh) {
    refreshInventoryMapData()
  }
  setScreensLayersVisibility(true)
}

export function deactivateInventoryScreensView(): void {
  const { map } = state.inventory
  if (!map) {
    return
  }
  setScreensLayersVisibility(false)
}

export function getAllInventoryScreensLayerIds(): string[] {
  return [SCREENS_CLUSTER_TEXT_LAYER_ID, SCREENS_POINT_LAYER_ID, SCREENS_CLUSTER_CIRCLE_LAYER_ID]
}

export function getClickableInventoryScreensLayerIds(): string[] {
  return [SCREENS_POINT_LAYER_ID, SCREENS_CLUSTER_CIRCLE_LAYER_ID]
}

export function createInventoryMapScreensLayers(map: Map): void {
  let cursorBeforeHover = getMapMouseCursor(map)
  map.addLayer({
    id: SCREENS_CLUSTER_CIRCLE_LAYER_ID,
    type: "circle",
    source: SCREENS_SOURCE_ID,
    filter: [">", ["get", "screen_count"], 1],
    paint: {
      "circle-color": state.inventory.screensStyleCircleColor,
      "circle-radius": state.inventory.screensStyleCircleRadius,
      "circle-opacity": MAP_INVENTORY_SCREENS_OPACITY,
      "circle-stroke-width": 1,
      "circle-stroke-color": MAP_COLOR
    }
  })
  map.addLayer({
    id: SCREENS_CLUSTER_TEXT_LAYER_ID,
    type: "symbol",
    source: SCREENS_SOURCE_ID,
    filter: [">", ["get", "screen_count"], 1],
    layout: {
      "text-field": ["get", "formatted_screen_count"],
      "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
      "text-size": 10,
      "text-ignore-placement": true
    },
    paint: {
      "text-color": state.inventory.screensStyleTextColor
    }
  })
  map.addLayer({
    id: SCREENS_POINT_LAYER_ID,
    type: "symbol",
    source: SCREENS_SOURCE_ID,
    layout: {
      "icon-image": state.inventory.screensStyleIconImage,
      "icon-allow-overlap": true,
      "icon-ignore-placement": true
    },
    filter: ["==", ["get", "screen_count"], 1]
  })
  map.on("mousemove", [SCREENS_CLUSTER_CIRCLE_LAYER_ID, SCREENS_POINT_LAYER_ID], event => {
    const currentCursor = getMapMouseCursor(map)
    if (event.features && event.features.length > 0) {
      if (currentCursor !== CURSOR_POINTER) {
        cursorBeforeHover = currentCursor
        setMapMouseCursor(map, CURSOR_POINTER)
      }
    } else if (currentCursor === CURSOR_POINTER) {
      setMapMouseCursor(map, cursorBeforeHover)
    }
  })
  map.on("mouseleave", [SCREENS_CLUSTER_CIRCLE_LAYER_ID, SCREENS_POINT_LAYER_ID], event => {
    const currentCursor = getMapMouseCursor(map)
    if (currentCursor === CURSOR_POINTER) {
      setMapMouseCursor(map, cursorBeforeHover)
    }
  })
  map.on("click", () => closeInventoryMapPopup())
  map.on("click", [SCREENS_CLUSTER_CIRCLE_LAYER_ID, SCREENS_POINT_LAYER_ID], e => {
    if (!e.features || e.features.length === 0) {
      closeInventoryMapPopup()
      return
    }
    openInventoryScreenMapPopup(e.features[0] as any)
  })
}

export function getScreensMapImages(): IMapImage[] {
  return [
    { name: SCREEN_IMAGE_1_ID, url: screenImage1 },
    { name: SCREEN_IMAGE_2_ID, url: screenImage2 },
    { name: SCREEN_IMAGE_3_ID, url: screenImage3 },
    { name: SCREEN_IMAGE_4_ID, url: screenImage4 },
    { name: SCREEN_IMAGE_5_ID, url: screenImage5 },
    { name: SCREEN_IMAGE_6_ID, url: screenImage6 },
    { name: SCREEN_IMAGE_7_ID, url: screenImage7 },
    { name: SCREEN_IMAGE_8_ID, url: screenImage8 },
    { name: SCREEN_IMAGE_9_ID, url: screenImage9 },
    { name: SCREEN_IMAGE_10_ID, url: screenImage10 }
  ]
}

export function setScreensLayerStyleForData(
  screenCountQuantiles: number[],
  impressionCountQuantiles: number[],
  screenImpressionsQuantiles: number[]
) {
  const { map } = state.inventory
  if (!map || !map.getLayer(SCREENS_CLUSTER_CIRCLE_LAYER_ID)) {
    return
  }
  // radius
  // radiusSteps ex:
  // [ "step", ["get", "screen_count"], DEFAULT_SCREENS_CLUSTER_RADIUS, quantiles[0], 16, quantiles[1], 17, quantiles[2], 18 ]
  let currentRadius = DEFAULT_SCREENS_CLUSTER_RADIUS
  if (screenCountQuantiles.length < 2) {
    map.setPaintProperty(SCREENS_CLUSTER_CIRCLE_LAYER_ID, "circle-radius", DEFAULT_SCREENS_CLUSTER_RADIUS)
    state.inventory.screensStyleCircleRadius = DEFAULT_SCREENS_CLUSTER_RADIUS
  } else {
    const radiusSteps: any[] = ["step", ["get", "screen_count"]]
    for (const screenCountQuantile of screenCountQuantiles) {
      radiusSteps.push(...[currentRadius++, screenCountQuantile])
    }
    radiusSteps.push(currentRadius)
    map.setPaintProperty(SCREENS_CLUSTER_CIRCLE_LAYER_ID, "circle-radius", radiusSteps)
    state.inventory.screensStyleCircleRadius = radiusSteps
  }
  // color
  const stepBackgroundColors = getImpressionsStepBackgroundColors(impressionCountQuantiles)
  const stepTextColors = getImpressionsStepTextColors(impressionCountQuantiles)
  if (impressionCountQuantiles.length < 2) {
    const circleColor = stepBackgroundColors.length > 0 ? stepBackgroundColors.pop() : MAP_COLOR
    const textColor = stepTextColors.length > 0 ? stepTextColors.pop() : THEME_PALETTE_SPECIAL_TEXT_COLOR
    map.setPaintProperty(SCREENS_CLUSTER_CIRCLE_LAYER_ID, "circle-color", circleColor)
    map.setPaintProperty(SCREENS_CLUSTER_TEXT_LAYER_ID, "text-color", textColor)
    state.inventory.screensStyleCircleColor = circleColor
    state.inventory.screensStyleTextColor = textColor
  } else {
    const backgroundColorSteps: any[] = ["step", ["get", "impressions"]]
    const textColorSteps: any[] = ["step", ["get", "impressions"]]
    for (
      let i = MAP_INVENTORY_QUANTILE_STEPS - impressionCountQuantiles.length;
      i < impressionCountQuantiles.length;
      i++
    ) {
      backgroundColorSteps.push(...[stepBackgroundColors[i], impressionCountQuantiles[i]])
      textColorSteps.push(...[stepTextColors[i], impressionCountQuantiles[i]])
    }
    backgroundColorSteps.push(stepBackgroundColors[impressionCountQuantiles.length - 1])
    textColorSteps.push(stepTextColors[impressionCountQuantiles.length - 1])
    map.setPaintProperty(SCREENS_CLUSTER_CIRCLE_LAYER_ID, "circle-color", backgroundColorSteps)
    map.setPaintProperty(SCREENS_CLUSTER_TEXT_LAYER_ID, "text-color", textColorSteps)
    state.inventory.screensStyleCircleColor = backgroundColorSteps
    state.inventory.screensStyleTextColor = textColorSteps
  }
  // images
  if (screenImpressionsQuantiles.length < 2) {
    const iconImage = SCREEN_IMAGE_10_ID
    map.setLayoutProperty(SCREENS_POINT_LAYER_ID, "icon-image", iconImage)
    state.inventory.screensStyleIconImage = iconImage
  } else {
    const iconImageSteps: any[] = ["step", ["get", "impressions"]]
    for (
      let i = MAP_INVENTORY_QUANTILE_STEPS - screenImpressionsQuantiles.length;
      i < screenImpressionsQuantiles.length;
      i++
    ) {
      iconImageSteps.push(...[`${SCREEN_IMAGE_ID_PREFIX}${i + 1}`, screenImpressionsQuantiles[i]])
    }
    iconImageSteps.push(SCREEN_IMAGE_10_ID)
    map.setLayoutProperty(SCREENS_POINT_LAYER_ID, "icon-image", iconImageSteps)
    state.inventory.screensStyleIconImage = iconImageSteps
  }
}

function setScreensLayersVisibility(isVisible: boolean): void {
  const { map } = state.inventory
  if (!map) {
    return
  }
  const layerIds = getAllInventoryScreensLayerIds()
  const visibility = isVisible ? "visible" : "none"
  if (!isVisible && isInventoryMapPopupOpened()) {
    closeInventoryMapPopup()
  }
  for (const layerId of layerIds) {
    if (!map.getLayer(layerId)) {
      continue
    }
    if (map.getLayoutProperty(layerId, "visibility") !== visibility) {
      map.setLayoutProperty(layerId, "visibility", visibility)
    }
  }
}
